import { appendAppLog } from '../platform/appLog';

/**
 * Was Apples Netzfehler bedeuten — als eine zusätzliche Zeile in der Konsole.
 *
 * In der Xcode-Konsole steht bei einem gescheiterten Aufruf nur die Rohform:
 *
 *     Task <…> finished with error [-1004] Error Domain=NSURLErrorDomain Code=-1004
 *     "Could not connect to the server."
 *
 * „Could not connect" liest sich wie ein Zertifikatsproblem, ist aber keines: bei `-1004` ist
 * die Verbindung auf **TCP-Ebene** abgelehnt worden, lange vor jedem TLS-Handschlag. Wer das
 * nicht weiss, prüft stundenlang die CA, während der Port falsch ist — am 2026-09-12 genau so
 * geschehen. Diese Datei schreibt die Bedeutung daneben.
 *
 * ⚠️ **Auf dem Gerät kommt die Zahl oft gar nicht an.** `CapacitorHttp` lehnt mit Apples
 * `localizedDescription` ab, nicht mit dem Code. Deshalb wird zusätzlich der Klartext erkannt.
 *
 * Die Codes sind `NSURLErrorDomain` (Foundation, seit iOS 2.0 unverändert).
 */

interface Deutung {
  /** Der Name der Konstanten — damit man bei Apple nachschlagen kann. */
  konstante: string;
  /** Was tatsächlich passiert ist. */
  bedeutung: string;
  /** Wonach als Erstes zu sehen ist. */
  pruefe: string;
}

const TABELLE: Record<number, Deutung> = {
  [-999]: {
    konstante: 'NSURLErrorCancelled',
    bedeutung: 'Der Aufruf wurde abgebrochen.',
    pruefe: 'Meist kein Fehler: die App hat selbst abgebrochen, etwa wegen der Zeitgrenze.',
  },
  [-1001]: {
    konstante: 'NSURLErrorTimedOut',
    bedeutung: 'Der Server hat nicht rechtzeitig geantwortet.',
    pruefe: 'Er ist erreichbar, aber zu langsam oder hängt. Last und WLAN-Güte ansehen.',
  },
  [-1003]: {
    konstante: 'NSURLErrorCannotFindHost',
    bedeutung: 'Der Name liess sich nicht auflösen.',
    pruefe: 'Adresse verschrieben, oder kein DNS im Netz. Mit der IP statt dem Namen versuchen.',
  },
  [-1004]: {
    konstante: 'NSURLErrorCannotConnectToHost',
    bedeutung:
      'Die Verbindung wurde abgelehnt — der Rechner ist da, aber auf diesem Port nimmt nichts an. ' +
      'Das geschieht auf TCP-Ebene, vor jedem TLS-Handschlag: mit Zertifikaten hat es nichts zu tun.',
    pruefe: 'Zuerst den Port: Classic 8443, Relay 8444. Dann, ob der Server überhaupt läuft.',
  },
  [-1005]: {
    konstante: 'NSURLErrorNetworkConnectionLost',
    bedeutung: 'Die Verbindung brach mitten im Aufruf ab.',
    pruefe: 'WLAN gewechselt, Funkloch, oder der Server hat die Verbindung zugemacht.',
  },
  [-1009]: {
    konstante: 'NSURLErrorNotConnectedToInternet',
    bedeutung: 'Das Gerät hat gar kein Netz.',
    pruefe: 'Flugmodus, WLAN aus, oder nicht angemeldet.',
  },
  [-1017]: {
    konstante: 'NSURLErrorCannotParseResponse',
    bedeutung: 'Die Antwort war keine gültige HTTP-Antwort.',
    pruefe: 'Spricht auf diesem Port wirklich HTTPS? Ein Klartext-Dienst sieht so aus.',
  },
  [-1022]: {
    konstante: 'NSURLErrorAppTransportSecurityRequiresSecureConnection',
    bedeutung: 'iOS hat den Aufruf verweigert, weil er unverschlüsselt war.',
    pruefe: 'Die Adresse beginnt mit http:// statt https://.',
  },
  [-1200]: {
    konstante: 'NSURLErrorSecureConnectionFailed',
    bedeutung: 'Der TLS-Handschlag scheiterte.',
    pruefe: 'Anders als -1004 ist hier tatsächlich TLS im Spiel: Protokollfassung oder Chiffren.',
  },
  [-1201]: {
    konstante: 'NSURLErrorServerCertificateHasBadDate',
    bedeutung: 'Das Zertifikat gilt noch nicht oder nicht mehr.',
    pruefe: 'Ablaufdatum des Zertifikats — und die Uhr des iPads.',
  },
  [-1202]: {
    konstante: 'NSURLErrorServerCertificateUntrusted',
    bedeutung: 'Das Zertifikat ist installiert, aber nicht als vertrauenswürdig eingestuft.',
    pruefe:
      'Einstellungen → Allgemein → Info → Zertifikatsvertrauen: dort den Schalter umlegen. ' +
      'Das Installieren des Profils allein genügt nicht — es sind zwei Schritte.',
  },
  [-1203]: {
    konstante: 'NSURLErrorServerCertificateHasUnknownRoot',
    bedeutung: 'Die ausstellende Stelle ist dem Gerät unbekannt.',
    pruefe: 'Die CA des Servers als Profil installieren, danach den Vertrauensschalter umlegen.',
  },
  [-1204]: {
    konstante: 'NSURLErrorServerCertificateNotYetValid',
    bedeutung: 'Das Zertifikat gilt erst später.',
    pruefe: 'Die Uhr des iPads geht vor, oder das Zertifikat ist auf die Zukunft ausgestellt.',
  },
};

/**
 * Apples Klartext, wenn der Code nicht mitkommt.
 *
 * Die Wortlaute stammen aus `localizedDescription` und sind auf einem englischsprachigen Gerät
 * gemessen. Ein deutschsprachiges iPad liefert andere — dann greift diese Liste nicht, und die
 * Deutung schweigt, statt zu raten.
 */
const AUS_KLARTEXT: ReadonlyArray<readonly [RegExp, number]> = [
  [/could not connect to the server/i, -1004],
  [/the request timed out/i, -1001],
  [/hostname could not be found|cannot find host/i, -1003],
  [/network connection was lost/i, -1005],
  [/internet connection appears to be offline/i, -1009],
  [/certificate for this server is invalid/i, -1202],
  [/an ssl error has occurred/i, -1200],
];

/** Die Zahl aus `code`, aus `Code=-1004` in der Meldung, oder aus Apples Klartext. */
function codeAus(fehler: unknown): number | null {
  if (typeof fehler !== 'object' || fehler === null) return null;

  const roh = (fehler as { code?: unknown }).code;
  if (typeof roh === 'number') return roh;
  if (typeof roh === 'string' && /^-\d+$/.test(roh)) return Number(roh);

  const meldung = (fehler as { message?: unknown }).message;
  if (typeof meldung !== 'string') return null;

  // Nur das Vorzeichen macht den Fehlercode aus — sonst träfe eine HTTP-Zahl oder eine
  // Bytezahl mit.
  const treffer = /-\d{3,4}\b/.exec(meldung);
  if (treffer !== null) return Number(treffer[0]);

  for (const [muster, code] of AUS_KLARTEXT) {
    if (muster.test(meldung)) return code;
  }
  return null;
}

/**
 * Die Deutung als eine Zeile — oder `null`, wenn nichts Sicheres zu sagen ist.
 *
 * `null` ist Absicht: eine falsche Fährte kostet mehr Zeit als gar keine.
 */
export function deuteNetzfehler(fehler: unknown): string | null {
  const code = codeAus(fehler);
  if (code === null) return null;

  const deutung = TABELLE[code];
  if (deutung === undefined) return null;

  return (
    'Netzfehler ' + String(code) + ' (' + deutung.konstante + '): ' + deutung.bedeutung +
    ' → ' + deutung.pruefe
  );
}

/**
 * Die Deutung hinausschreiben — in die Konsole und ins Dateilog.
 *
 * Beides, weil die Xcode-Konsole nur da ist, solange das iPad am Kabel hängt. Im Prüfungsbetrieb
 * hängt es das nicht, und dann ist `Documents/log.txt` der einzige Ort, an dem hinterher noch
 * steht, woran es lag.
 *
 * Wirft nie: eine Diagnosehilfe, die selbst zum Fehler wird, verdeckt genau das, wobei sie
 * helfen soll.
 */
export function meldeNetzfehler(fehler: unknown): void {
  const deutung = deuteNetzfehler(fehler);
  if (deutung === null) return;
  // ⚠️ **Kein eigenes `console.warn` mehr** (2026-09-25): `appendAppLog` gibt jede Zeile
  // selbst auf der Konsole aus. Beides nebeneinander schrieb die Deutung zweimal ans Kabel
  // und damit auch zweimal ins Dateilog.
  void appendAppLog(deutung, 'warn').catch(() => undefined);
}
